import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { InstalledApp } from '../../hooks/useAppSystem';
import { getAppIcon, isCustomAppIcon } from '../../lib/appIcons';

interface DashboardAppSnippetProps {
  app: InstalledApp;
  children?: ReactNode;
  /** Override navigation target for the header icon (defaults to app route_prefix). */
  appRoute?: string;
  /** Optional content beside the app icon (e.g. sales metrics). */
  headerEnd?: ReactNode;
  /** Shown immediately to the right of the app icon in the header. */
  headerBesideIcon?: ReactNode;
  /** Shown on the right of the mobile title row (e.g. calendar + create). */
  headerActions?: ReactNode;
  /** Mobile: icon + title + actions on one row; metrics full width below. */
  compactMobileHeader?: boolean;
}

const SNIPPET_ROUTE_BY_SLUG: Record<string, string> = {
  calendar: '/calendarV2',
  'mobile-calendar': '/mobile-calendar',
  crm: '/crm',
  marketing: '/marketing',
  inventory: '/stock',
};

/** Shell for a control-centre panel tied to one installed app. */
export default function DashboardAppSnippet({
  app,
  children,
  appRoute,
  headerEnd,
  headerBesideIcon,
  headerActions,
  compactMobileHeader = false,
}: DashboardAppSnippetProps) {
  const appName = app.app?.name ?? 'App';
  const appSlug = app.app?.slug;
  const iconName = app.app?.icon ?? 'HelpCircle';
  const headerRoute =
    appRoute ?? app.app?.route_prefix ?? (appSlug ? SNIPPET_ROUTE_BY_SLUG[appSlug] : undefined);

  const usesPngIcon = isCustomAppIcon(appSlug);
  const iconContent = getAppIcon(
    iconName,
    'h-9 w-9',
    appSlug,
    usesPngIcon ? { tile: true } : undefined
  );
  const pngIconLinkClassName =
    'block h-9 w-9 shrink-0 overflow-hidden rounded-[22%] transition-transform touch-manipulation hover:opacity-90 active:scale-[0.98]';
  const lucideIconClassName =
    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-50 border border-gray-100 text-gray-800 transition-colors touch-manipulation hover:bg-gray-100 hover:border-gray-200 active:scale-[0.98]';

  const renderBesideIcon = (slot: 'mobile' | 'desktop') => {
    if (!headerBesideIcon) return null;
    const content = isValidElement(headerBesideIcon)
      ? cloneElement(headerBesideIcon as ReactElement, { key: slot })
      : headerBesideIcon;
    return <div className="shrink-0">{content}</div>;
  };

  const iosTitleFont =
    "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', system-ui, sans-serif";

  return (
    <section
      className="relative z-0 overflow-hidden"
      data-app-slug={appSlug}
      data-control-centre-panel
      aria-label={`${appName} dashboard`}
    >
      <header className="flex flex-col gap-2.5 p-3 min-w-0 sm:gap-2.5 sm:p-3 sm:flex-row sm:items-stretch">
        {compactMobileHeader ? (
          <>
            <div className="flex items-center justify-between gap-2 min-w-0 sm:hidden">
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                {headerRoute ? (
                  <Link
                    to={headerRoute}
                    className={`shrink-0 ${usesPngIcon ? pngIconLinkClassName : lucideIconClassName}`}
                    aria-label={`Open ${appName}`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {iconContent}
                  </Link>
                ) : (
                  <div
                    className={`shrink-0 ${usesPngIcon ? pngIconLinkClassName : lucideIconClassName}`}
                    aria-hidden
                  >
                    {iconContent}
                  </div>
                )}
                {renderBesideIcon('mobile')}
                <span
                  className="text-[17px] font-semibold text-gray-900 truncate min-w-0 leading-tight"
                  style={{ fontFamily: iosTitleFont, letterSpacing: '-0.015em' }}
                >
                  {appName}
                </span>
              </div>
              {headerActions ? (
                <div className="flex items-center gap-1.5 shrink-0 min-w-0">
                  {headerActions}
                </div>
              ) : null}
            </div>
            {headerEnd ? (
              <div className="w-full min-w-0 sm:hidden">{headerEnd}</div>
            ) : null}
          </>
        ) : null}

        <div
          className={`items-center gap-2.5 min-w-0 shrink-0 ${
            compactMobileHeader ? 'hidden sm:flex' : 'flex'
          }`}
        >
          {headerRoute ? (
            <Link
              to={headerRoute}
              className={`shrink-0 ${usesPngIcon ? pngIconLinkClassName : lucideIconClassName}`}
              aria-label={`Open ${appName}`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {iconContent}
            </Link>
          ) : (
            <div
              className={`shrink-0 ${usesPngIcon ? pngIconLinkClassName : lucideIconClassName}`}
              aria-hidden
            >
              {iconContent}
            </div>
          )}
          {renderBesideIcon('desktop')}
          {!compactMobileHeader ? (
            <span
              className="sm:hidden text-[17px] font-semibold text-gray-900 truncate min-w-0 leading-tight"
              style={{ fontFamily: iosTitleFont, letterSpacing: '-0.015em' }}
            >
              {appName}
            </span>
          ) : null}
        </div>
        {headerEnd ? (
          <div
            className={`w-full min-w-0 sm:flex-1 sm:self-center ${
              compactMobileHeader ? 'hidden sm:block' : ''
            }`}
          >
            {headerEnd}
          </div>
        ) : null}
      </header>
      <div className="control-centre-panel__body">{children}</div>
    </section>
  );
}
